import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/authService';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await authService.login(email, password);
      
      // Always redirect to app domain after successful login
      const appUrl = process.env.REACT_APP_APP_URL || (window.location.hostname.includes('-staging') ? window.location.origin : window.location.origin.replace('signalcue.ai', 'app.signalcue.ai'));
      const isSameOrigin = new URL(appUrl).origin === window.location.origin;
      
      // Build redirect URL with token and user data for cross-domain transfer
      const params = new URLSearchParams();
      params.append('token', response.access_token);
      params.append('user', JSON.stringify(response.user));
      
      // Determine redirect path based on user role
      let redirectPath = '/candidates'; // Default for recruiter
      if (response.user?.role === 'admin') {
        redirectPath = '/admin';
      } else if (response.user?.role === 'manager') {
        redirectPath = '/candidates';
      }
      
      // Check if password change is required
      if (response.user?.must_change_password) {
        params.append('changePassword', 'true');
      }
      
      // Always redirect to app domain (app.signalcue.ai) after login
      if (isSameOrigin) {
        // Local development: use React Router navigation
        navigate(`${redirectPath}?${params.toString()}`);
      } else {
        // Production: redirect to app domain with token in URL
        window.location.href = `${appUrl}${redirectPath}?${params.toString()}`;
      }
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      {/* Centered modal dialog */}
      <div className="bg-white p-8 rounded-xl shadow-xl w-full max-w-md border border-gray-200">
        <div className="text-center mb-8">
          {/* SignalCue logo at top */}
          <div className="flex justify-center items-center mb-6">
            <img src="/sc_white.png" alt="SignalCue" className="h-[30px] w-auto" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Welcome Back</h1>
          <p className="text-gray-600 text-sm">Sign in to access your recruitment intelligence</p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-gray-700 text-sm font-medium mb-2">
              Email Address
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00C6AE] focus:border-[#00C6AE] outline-none transition"
              placeholder="you@company.com"
              required
              autoComplete="email"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label htmlFor="password" className="block text-gray-700 text-sm font-medium">
                Password
              </label>
              <button 
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  // TODO: Implement forgot password functionality
                  alert('Forgot password functionality coming soon');
                }}
                className="text-sm text-[#00C6AE] hover:text-[#00C6AE]/80 font-medium"
              >
                Forgot password?
              </button>
            </div>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00E8DA] focus:border-[#00E8DA] outline-none transition"
              placeholder="Enter your password"
              required
              autoComplete="current-password"
            />
          </div>

          <div className="flex items-center">
            <input
              id="remember"
              type="checkbox"
              className="h-4 w-4 text-[#00C6AE] focus:ring-[#00C6AE] border-gray-300 rounded"
            />
            <label htmlFor="remember" className="ml-2 block text-sm text-gray-700">
              Remember me
            </label>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#00C6AE] text-black py-3 rounded-lg font-semibold hover:bg-[#00C6AE]/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md"
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Signing in...
              </span>
            ) : (
              'Sign In'
            )}
          </button>
        </form>

        <p className="text-center text-gray-500 text-xs mt-6">
          © 2026 SignalCue. All rights reserved.
        </p>
      </div>
    </div>
  );
}